import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Grid,
  Avatar,
  Chip,
  Snackbar,
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../config/api';
import {
  StyledTableContainer,
  TableSkeleton,
  FilterBar,
  SearchInput,
  StyledPagination,
  EmptyState,
  StatusChip,
} from '../components/TableShared';

const ff = "'Poppins', sans-serif";

function formatDateNice(dateString) {
  if (!dateString) return 'N/A';
  try {
    return format(new Date(dateString), 'MMM dd, yyyy hh:mm a');
  } catch {
    return 'N/A';
  }
}

function truncate(str, maxLen = 50) {
  if (!str) return 'N/A';
  return str.length > maxLen ? str.slice(0, maxLen) + '...' : str;
}

// ----- MessageDetailDialog -----
function MessageDetailDialog({ open, message, onClose }) {
  if (!message) return null;

  const InfoRow = ({ label, value }) => (
    <>
      <Grid item xs={4}>
        <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ fontFamily: ff }}>
          {label}
        </Typography>
      </Grid>
      <Grid item xs={8}>
        <Typography variant="body2" sx={{ fontFamily: ff }}>{value || 'N/A'}</Typography>
      </Grid>
    </>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily: ff }}>
        Message Details
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Sender Info */}
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ fontFamily: ff }}>
          Sender
        </Typography>
        <Grid container spacing={1} sx={{ mb: 2 }} alignItems="center">
          <Grid item xs={4}>
            <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ fontFamily: ff }}>
              Avatar
            </Typography>
          </Grid>
          <Grid item xs={8}>
            <Avatar
              src={message.sender?.avatarUrl}
              alt={message.sender?.fullName || 'Sender'}
              sx={{ width: 40, height: 40 }}
            />
          </Grid>
          <InfoRow label="Name" value={message.sender?.fullName} />
          <InfoRow label="Email" value={message.sender?.email} />
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Receiver Info */}
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ fontFamily: ff }}>
          Receiver
        </Typography>
        <Grid container spacing={1} sx={{ mb: 2 }} alignItems="center">
          <Grid item xs={4}>
            <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ fontFamily: ff }}>
              Avatar
            </Typography>
          </Grid>
          <Grid item xs={8}>
            <Avatar
              src={message.receiver?.avatarUrl}
              alt={message.receiver?.fullName || 'Receiver'}
              sx={{ width: 40, height: 40 }}
            />
          </Grid>
          <InfoRow label="Name" value={message.receiver?.fullName} />
          <InfoRow label="Email" value={message.receiver?.email} />
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Message Content */}
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ fontFamily: ff }}>
          Message
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#f9f9f9' }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: ff }}>
            {message.content || 'No content'}
          </Typography>
        </Paper>

        {/* Meta Info */}
        <Grid container spacing={1}>
          <Grid item xs={4}>
            <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ fontFamily: ff }}>
              Read Status
            </Typography>
          </Grid>
          <Grid item xs={8}>
            <Chip
              label={message.isRead ? 'Read' : 'Unread'}
              color={message.isRead ? 'success' : 'warning'}
              size="small"
            />
          </Grid>
          <InfoRow label="Sent At" value={formatDateNice(message.createdAt)} />
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} sx={{ fontFamily: ff }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ----- Chats Page -----
export default function Chats() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalMessages, setTotalMessages] = useState(0);

  // Search state
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const debounceTimer = useRef(null);

  // Detail dialog state
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };
      if (search) params.search = search;
      const response = await api.get('/admin/messages', { params });
      const data = response.data.data || response.data;
      setMessages(data.messages || []);
      setTotalMessages(data.total || data.messages?.length || 0);
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError('Failed to load messages. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Debounced search handler
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      setSearch(value);
      setPage(0);
    }, 300);
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (message) => {
    setSelectedMessage(message);
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedMessage(null);
  };

  const handleDeleteMessage = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/messages/${deleteTarget.id}`);
      setSnackbar({ open: true, message: 'Message deleted', severity: 'success' });
      setDeleteTarget(null);
      fetchMessages();
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to delete message', severity: 'error' });
    }
  };

  return (
    <Box>
      <FilterBar>
        <SearchInput
          placeholder="Search by message content..."
          value={searchInput}
          onChange={handleSearchChange}
        />
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <TableSkeleton columns={5} rows={8} />
      ) : (
        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sender</TableCell>
                <TableCell>Receiver</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Read Status</TableCell>
                <TableCell>Sent At</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messages.length === 0 ? (
                <EmptyState colSpan={6} message="No messages found." />
              ) : (
                messages.map((message) => (
                  <TableRow
                    key={message.id}
                    onClick={() => handleRowClick(message)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontFamily: ff }}>
                        <Avatar
                          src={message.sender?.avatarUrl}
                          alt={message.sender?.fullName || 'S'}
                          sx={{ width: 28, height: 28 }}
                        />
                        {message.sender?.fullName || message.sender?.email || 'N/A'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontFamily: ff }}>
                        <Avatar
                          src={message.receiver?.avatarUrl}
                          alt={message.receiver?.fullName || 'R'}
                          sx={{ width: 28, height: 28 }}
                        />
                        {message.receiver?.fullName || message.receiver?.email || 'N/A'}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: ff }}>
                      {truncate(message.content, 60)}
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        label={message.isRead ? 'Read' : 'Unread'}
                        color={message.isRead ? '#22C55E' : '#F59E0B'}
                        bgColor={message.isRead ? '#F0FDF4' : '#FFFBEB'}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: ff }}>{formatDateNice(message.createdAt)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        sx={{ color: '#E53E3E' }}
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(message); }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <StyledPagination
            count={totalMessages}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </StyledTableContainer>
      )}

      {/* Message Detail Dialog */}
      <MessageDetailDialog
        open={detailOpen}
        message={selectedMessage}
        onClose={handleCloseDetail}
      />

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle sx={{ fontFamily: ff, fontWeight: 600 }}>Delete Message?</DialogTitle>
        <DialogContent>
          <Box sx={{ fontFamily: ff, fontSize: 13, color: '#718096' }}>
            This will permanently remove this message from {deleteTarget?.sender?.fullName || 'sender'} to {deleteTarget?.receiver?.fullName || 'receiver'}.
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ fontFamily: ff, textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleDeleteMessage} variant="contained" color="error" sx={{ fontFamily: ff, textTransform: 'none' }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(p => ({ ...p, open: false }))}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
